import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import {
  clearEventCltOverride,
  createSbaCalendar,
  deleteSbaCalendar,
  getSbaCalendar,
  getSbaCalendarEventDetail,
  listSbaCalendarEventDates,
  listSbaCalendarEvents,
  listSbaCalendars,
  recomputeSbaCalendar,
  recomputeSbaCalendarEvent,
  setEventCltOverride,
  setSbaCalendarTemplate,
  type ListSbaCalendarsParams,
  type SbaCalendarEventDatesQuery,
} from '@/api/sbaCalendars'
import type {
  ComputeStatus,
  ComputeStatusEvent,
  PaginatedSbaCalendarsResponse,
  SbaCalendarCreate,
  SbaCalendarRead,
} from '@/api/clt.types'
import { useSSE, type SSEEvent } from '@/hooks/useSSE'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

const KEY = ['sba-calendars'] as const

/**
 * Optimistically marks the cached calendar as pending so the dates UI hides
 * immediately on mutate, before the SSE 'computing' frame arrives. The SSE
 * stream and subsequent refetches will replace this with the real status.
 */
function markCalendarPending(
  queryClient: QueryClient,
  calendarId: string
): void {
  queryClient.setQueryData<SbaCalendarRead>([...KEY, calendarId], prev =>
    prev ? { ...prev, status: 'pending', last_computed_at: null } : prev
  )
}

/**
 * Lists calendars from the unified `/calendars` endpoint. SBA and standard
 * assortment calendars share the endpoint after the merge — both are returned.
 */
export function useSbaCalendars(params: ListSbaCalendarsParams = {}) {
  return useQuery({
    queryKey: [...KEY, 'list', params],
    queryFn: () => listSbaCalendars(params),
    select: (response: PaginatedSbaCalendarsResponse) => response.data,
  })
}

export function useCreateSbaCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SbaCalendarCreate) => createSbaCalendar(payload),
    onSuccess: created => {
      queryClient.setQueryData<SbaCalendarRead>([...KEY, created.id], {
        ...created,
        status: 'pending',
        last_computed_at: null,
      })
      queryClient.invalidateQueries({ queryKey: KEY })
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create calendar'))
    },
  })
}

export function useSbaCalendar(
  id: string | undefined,
  options?: { refetchInterval?: false }
) {
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: () => getSbaCalendar(id as string),
    enabled: !!id,
    // SSE-down fallback: poll every 5s while not in a terminal state so we
    // catch 'ready' if the SSE stream drops. Suppressed when SSE is healthy.
    refetchInterval:
      options?.refetchInterval === false
        ? false
        : query => {
            const status = query.state.data?.status
            // Non-SBA calendars have status=null and never compute; treat as terminal.
            if (
              status == null ||
              status === 'ready' ||
              status === 'error' ||
              status === 'failed'
            )
              return false
            return 5000
          },
  })
}

export function useSbaCalendarEvents(calendarId: string | undefined) {
  return useQuery({
    queryKey: [...KEY, calendarId, 'events'],
    queryFn: () => listSbaCalendarEvents(calendarId as string),
    enabled: !!calendarId,
  })
}

export function useSbaCalendarEventDetail(
  calendarId: string | undefined,
  eventId: string | undefined
) {
  return useQuery({
    queryKey: [...KEY, calendarId, 'events', eventId, 'detail'],
    queryFn: () =>
      getSbaCalendarEventDetail(calendarId as string, eventId as string),
    enabled: !!calendarId && !!eventId,
  })
}

export function useSbaCalendarEventDates(
  calendarId: string | undefined,
  eventId: string | undefined,
  params: SbaCalendarEventDatesQuery | undefined
) {
  return useQuery({
    queryKey: [...KEY, calendarId, 'events', eventId, 'dates', params],
    queryFn: () =>
      listSbaCalendarEventDates(
        calendarId as string,
        eventId as string,
        params as SbaCalendarEventDatesQuery
      ),
    enabled: !!calendarId && !!eventId && !!params?.milestone_id,
  })
}

function invalidateCalendar(
  queryClient: ReturnType<typeof useQueryClient>,
  calendarId: string
) {
  queryClient.invalidateQueries({ queryKey: [...KEY, calendarId] })
  queryClient.invalidateQueries({ queryKey: KEY })
}

export function useSetSbaCalendarTemplate(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (templateId: string | null) =>
      setSbaCalendarTemplate(calendarId, templateId),
    onSuccess: () => {
      markCalendarPending(queryClient, calendarId)
      invalidateCalendar(queryClient, calendarId)
      void recomputeSbaCalendar(calendarId).catch(() => undefined)
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to set calendar template'))
    },
  })
}

interface BulkTemplateResult {
  succeeded: string[]
  failed: { calendarId: string; error: string }[]
}

export function useSetSbaCalendarsTemplateBulk() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      calendarIds,
      templateId,
    }: {
      calendarIds: string[]
      templateId: string
    }): Promise<BulkTemplateResult> => {
      const results = await Promise.allSettled(
        calendarIds.map(id => setSbaCalendarTemplate(id, templateId))
      )
      const succeeded: string[] = []
      const failed: BulkTemplateResult['failed'] = []
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') succeeded.push(calendarIds[i])
        else
          failed.push({
            calendarId: calendarIds[i],
            error: getErrorMessage(r.reason, 'Failed'),
          })
      })
      void Promise.allSettled(
        succeeded.map(id => recomputeSbaCalendar(id).catch(() => undefined))
      )
      if (succeeded.length === 0) throw new Error(failed[0].error)
      return { succeeded, failed }
    },
    onMutate: ({ calendarIds }) => {
      calendarIds.forEach(id => markCalendarPending(queryClient, id))
    },
    onSuccess: result => {
      result.succeeded.forEach(id => invalidateCalendar(queryClient, id))
      queryClient.invalidateQueries({ queryKey: KEY })
      if (result.failed.length === 0) {
        toast.success(
          `Template applied to ${result.succeeded.length} calendar(s)`
        )
      } else {
        toast.warning(
          `Applied to ${result.succeeded.length}; ${result.failed.length} failed`
        )
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to apply template'))
    },
  })
}

export function useSetEventCltOverride(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      eventId,
      templateId,
    }: {
      eventId: string
      templateId: string
    }) => setEventCltOverride(calendarId, eventId, templateId),
    onSuccess: () => {
      invalidateCalendar(queryClient, calendarId)
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to set event override'))
    },
  })
}

export function useRecomputeSbaCalendar(calendarId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => {
      if (!calendarId) throw new Error('Calendar id required')
      return recomputeSbaCalendar(calendarId)
    },
    onSuccess: () => {
      if (calendarId) {
        markCalendarPending(queryClient, calendarId)
        invalidateCalendar(queryClient, calendarId)
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to trigger recompute'))
    },
  })
}

export function useRecomputeSbaCalendarEvent(
  calendarId: string,
  eventId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => recomputeSbaCalendarEvent(calendarId, eventId),
    onSuccess: () => {
      invalidateCalendar(queryClient, calendarId)
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to trigger event recompute'))
    },
  })
}

export function useDeleteSbaCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSbaCalendar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY })
      toast.success('Calendar deleted successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete calendar'))
    },
  })
}

export function useClearEventCltOverride(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: string) => clearEventCltOverride(calendarId, eventId),
    onSuccess: () => {
      invalidateCalendar(queryClient, calendarId)
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to clear event override'))
    },
  })
}

export interface CalendarComputeStatusState {
  status: ComputeStatus | null
  progress: number | null
  detail: string | null
  isHealthy: boolean
}

/**
 * Subscribes to the calendar compute SSE stream. When the backend reports
 * `ready`, refetches events + dates so the UI catches up automatically.
 */
export function useSbaCalendarComputeStream(calendarId: string | undefined) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<CalendarComputeStatusState>({
    status: null,
    progress: null,
    detail: null,
    isHealthy: true,
  })

  const handleMessage = useCallback(
    (event: SSEEvent) => {
      if (event.type !== 'compute-status' || !calendarId) return
      const payload = event.data as Partial<ComputeStatusEvent>
      setState(prev => ({
        status: payload.status ?? null,
        progress: payload.progress ?? null,
        detail: payload.detail ?? null,
        isHealthy: prev.isHealthy,
      }))
      if (payload.status === 'ready') {
        invalidateCalendar(queryClient, calendarId)
      } else if (payload.status === 'error' || payload.status === 'failed') {
        toast.error(payload.error ?? 'Calendar compute failed')
      }
    },
    [calendarId, queryClient]
  )

  const handleHealthChange = useCallback((healthy: boolean) => {
    setState(prev => ({ ...prev, isHealthy: healthy }))
  }, [])

  useSSE({
    url: calendarId ? `/calendars/${calendarId}/events/stream` : null,
    enabled: !!calendarId,
    onMessage: handleMessage,
    onHealthChange: handleHealthChange,
    retry: true,
    retryInterval: 3000,
    maxRetryInterval: 60_000,
    autoDisconnectOnTerminal: false,
  })

  return state
}

export interface CalendarFreshnessState {
  calendar: SbaCalendarRead | undefined
  isLoading: boolean
  status: ComputeStatus | null
  progress: number | null
  detail: string | null
  isReady: boolean
  isComputing: boolean
  isFailed: boolean
  isHealthy: boolean
}

/**
 * Single source of truth for the calendar record plus whether its dates are
 * safe to render. Owns the only `useSbaCalendar` subscription on the page so
 * the SSE-healthy poll suppression isn't defeated by a sibling observer
 * (React Query merges intervals across observers — shortest wins).
 *
 * Merges the live SSE stream (preferred) with the persisted `status` on the
 * calendar record (fallback when SSE is silent or down). The calendar query
 * itself polls every 5s while status is not `ready`/`error`, so even if the
 * SSE stream drops we will still catch the transition.
 */
export function useCalendarFreshness(
  calendarId: string | undefined
): CalendarFreshnessState {
  const sse = useSbaCalendarComputeStream(calendarId)
  const { data: calendar, isLoading } = useSbaCalendar(calendarId, {
    refetchInterval: sse.isHealthy ? false : undefined,
  })
  const status = sse.status ?? calendar?.status ?? null
  return {
    calendar,
    isLoading,
    status,
    progress: sse.progress,
    detail: sse.detail,
    isReady: status === 'ready',
    isComputing: status === 'pending' || status === 'computing',
    isFailed: status === 'error' || status === 'failed',
    isHealthy: sse.isHealthy,
  }
}
